/**
 * Performance Optimization Utilities
 * Image optimization, code splitting, and caching strategies
 * Following web performance best practices
 */

import { trackPerformance } from './analytics';

/**
 * Image Optimization Utilities
 */
export class ImageOptimizer {
  /**
   * Lazy load images with intersection observer
   */
  static setupLazyLoading(): void {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      this.fallbackLazyLoading();
      return;
    }

    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          this.loadImage(img);
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01,
    });

    // Observe all images with data-src attribute
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }

  /**
   * Load image and track performance
   */
  private static loadImage(img: HTMLImageElement): void {
    const startTime = performance.now();
    const src = img.getAttribute('data-src');
    
    if (!src) return;

    const image = new Image();
    
    image.onload = () => {
      const loadTime = performance.now() - startTime;
      trackPerformance('image_load_time', loadTime);
      
      img.src = src;
      img.removeAttribute('data-src');
      img.classList.add('loaded');
    };

    image.onerror = () => {
      trackPerformance('image_load_error', 1);
    };

    image.src = src;
  }

  /**
   * Fallback lazy loading for older browsers
   */
  private static fallbackLazyLoading(): void {
    let ticking = false;
    
    const loadImages = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const images = document.querySelectorAll('img[data-src]');
          // Batch read all layout properties first to avoid forced reflows
          const imageData = Array.from(images).map(img => ({
            element: img,
            rect: img.getBoundingClientRect()
          }));
          
          // Then perform all DOM writes
          imageData.forEach(({ element, rect }) => {
            if (rect.top <= window.innerHeight + 100) {
              this.loadImage(element as HTMLImageElement);
            }
          });
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', loadImages, { passive: true });
    window.addEventListener('resize', loadImages, { passive: true });
    loadImages(); // Load initial visible images
  }

  /**
   * Generate responsive image srcset
   */
  static generateSrcSet(baseUrl: string, widths: number[]): string {
    return widths
      .map(width => `${baseUrl}?w=${width} ${width}w`)
      .join(', ');
  }

  /**
   * Optimize image format based on browser support
   */
  static getOptimalFormat(): 'webp' | 'avif' | 'jpeg' {
    if (typeof window === 'undefined') return 'jpeg';

    // Check AVIF support
    const avifCanvas = document.createElement('canvas');
    avifCanvas.width = 1;
    avifCanvas.height = 1;
    const avifDataURL = avifCanvas.toDataURL('image/avif');
    if (avifDataURL.indexOf('data:image/avif') === 0) {
      return 'avif';
    }

    // Check WebP support
    const webpCanvas = document.createElement('canvas');
    webpCanvas.width = 1;
    webpCanvas.height = 1;
    const webpDataURL = webpCanvas.toDataURL('image/webp');
    if (webpDataURL.indexOf('data:image/webp') === 0) {
      return 'webp';
    }

    return 'jpeg';
  }

  /**
   * Preload critical images
   */
  static preloadCriticalImages(urls: string[]): void {
    urls.forEach(url => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
    });
  }
}

/**
 * Code Splitting Utilities
 */
export class CodeSplitter {
  /**
   * Dynamic import with loading state
   */
  static async loadComponent(importFn: () => Promise<any>, fallback?: React.ComponentType): Promise<any> {
    const startTime = performance.now();
    
    try {
      const module = await importFn();
      const loadTime = performance.now() - startTime;
      trackPerformance('component_load_time', loadTime);
      
      return module.default || module;
    } catch (error) {
      trackPerformance('component_load_error', 1);
      
      if (fallback) {
        return fallback;
      }
      
      throw error;
    }
  }

  /**
   * Preload component for faster navigation
   */
  static preloadComponent(importFn: () => Promise<any>): void {
    // Start loading but don't wait for it
    importFn().catch(error => {
      // Silent fail for preload
    });
  }

  /**
   * Route-based code splitting
   */
  static createRouteLoader(routePath: string) {
    return () => import(`../pages/${routePath}.tsx`);
  }
}

/**
 * Caching Strategies
 */
export class CacheManager {
  private static cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  /**
   * Set cache entry with TTL
   */
  static set(key: string, data: any, ttl: number = 300000): void { // Default 5 minutes
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  /**
   * Get cache entry
   */
  static get(key: string): any | null {
    const entry = this.cache.get(key);
    
    if (!entry) return null;
    
    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  /**
   * Clear expired cache entries
   */
  static cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache
   */
  static clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  static getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

/**
 * Service Worker Utilities
 */
export class ServiceWorkerManager {
  /**
   * Register service worker for caching
   * Disabled for now to prevent errors
   */
  static async register(): Promise<ServiceWorkerRegistration | null> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      // Service Worker registered successfully
      return registration;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update service worker
   */
  static async update(): Promise<void> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    await registration.update();
  }
}

/**
 * Performance Monitoring
 */
export class PerformanceMonitor {
  private static metrics: Map<string, number[]> = new Map();

  /**
   * Track performance metric
   */
  static trackMetric(name: string, value: number): void {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    this.metrics.get(name)!.push(value);
    
    // Send to analytics
    trackPerformance(name, value);
  }

  /**
   * Get metric statistics
   */
  static getMetricStats(name: string): { avg: number; min: number; max: number; count: number } | null {
    const values = this.metrics.get(name);
    if (!values || values.length === 0) return null;

    const sum = values.reduce((a, b) => a + b, 0);
    const avg = sum / values.length;
    const min = Math.min(...values);
    const max = Math.max(...values);

    return { avg, min, max, count: values.length };
  }

  /**
   * Monitor Core Web Vitals
   */
  static monitorCoreWebVitals(): void {
    if (typeof window === 'undefined') return;

    // Monitor Largest Contentful Paint (LCP)
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.trackMetric('lcp', lastEntry.startTime);
    });
    observer.observe({ entryTypes: ['largest-contentful-paint'] });

    // Monitor First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        const fidEntry = entry as any;
        if (fidEntry.processingStart) {
          this.trackMetric('fid', fidEntry.processingStart - fidEntry.startTime);
        }
      });
    });
    fidObserver.observe({ entryTypes: ['first-input'] });

    // Monitor Cumulative Layout Shift (CLS)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        const clsEntry = entry as any;
        if (!clsEntry.hadRecentInput) {
          clsValue += clsEntry.value || 0;
        }
      });
      this.trackMetric('cls', clsValue);
    });
    clsObserver.observe({ entryTypes: ['layout-shift'] });
  }

  /**
   * Monitor resource loading
   */
  static monitorResourceLoading(): void {
    if (typeof window === 'undefined') return;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.entryType === 'resource') {
          const resourceEntry = entry as PerformanceResourceTiming;
          this.trackMetric('resource_load_time', resourceEntry.duration);
          
          // Track specific resource types
          if (resourceEntry.initiatorType === 'img') {
            this.trackMetric('image_load_time', resourceEntry.duration);
          } else if (resourceEntry.initiatorType === 'script') {
            this.trackMetric('script_load_time', resourceEntry.duration);
          } else if (resourceEntry.initiatorType === 'css') {
            this.trackMetric('css_load_time', resourceEntry.duration);
          }
        }
      });
    });
    observer.observe({ entryTypes: ['resource'] });
  }
}

/**
 * Bundle Analysis
 */
export class BundleAnalyzer {
  /**
   * Get bundle size information
   */
  static getBundleInfo(): { size: number; chunks: number } {
    if (typeof window === 'undefined') {
      return { size: 0, chunks: 0 };
    }

    // This would typically come from webpack bundle analyzer
    // For now, we'll estimate based on performance entries
    const resources = performance.getEntriesByType('resource');
    const scripts = resources.filter(r => r.name.includes('.js'));
    const totalSize = scripts.reduce((sum, script) => sum + (script as any).transferSize || 0, 0);

    return {
      size: totalSize,
      chunks: scripts.length,
    };
  }
}

/**
 * Initialize all performance optimizations
 */
export const initializePerformanceOptimizations = (): void => {
  // Setup lazy loading
  ImageOptimizer.setupLazyLoading();
  
  // Monitor performance
  PerformanceMonitor.monitorCoreWebVitals();
  PerformanceMonitor.monitorResourceLoading();
  
  // No service worker: remove any previously registered one (it cached pages
  // cache-first and caused blank pages after deploys)
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then((regs) => regs.forEach((reg) => reg.unregister()))
      .catch(() => {});
  }
  if ('caches' in window) {
    caches.keys()
      .then((keys) => keys.forEach((key) => caches.delete(key)))
      .catch(() => {});
  }
  
  // Cleanup cache periodically
  setInterval(() => {
    CacheManager.cleanup();
  }, 60000); // Every minute
};

export default {
  ImageOptimizer,
  CodeSplitter,
  CacheManager,
  ServiceWorkerManager,
  PerformanceMonitor,
  BundleAnalyzer,
  initializePerformanceOptimizations,
}; 